import React from 'react';

const skeletonBase = 'animate-pulse bg-white/10 rounded';

export interface SkeletonProps {
  className?: string;
}

/** Single line of placeholder text */
export const SkeletonLine: React.FC<SkeletonProps & { width?: string | number; height?: string | number }> = ({
  className = '',
  width,
  height = '0.75rem',
}) => (
  <div
    className={`${skeletonBase} ${className}`}
    style={{ width: width ?? '100%', height: typeof height === 'number' ? `${height}px` : height }}
  />
);

/** Rectangular block */
export const SkeletonBox: React.FC<SkeletonProps & { width?: string | number; height?: string | number }> = ({
  className = '',
  width,
  height = '4rem',
}) => (
  <div
    className={`${skeletonBase} ${className}`}
    style={{ width: width ?? '100%', height: typeof height === 'number' ? `${height}px` : height }}
  />
);

/** Circular placeholder (avatar, icon) */
export const SkeletonCircle: React.FC<SkeletonProps & { size?: string | number }> = ({
  className = '',
  size = '2.5rem',
}) => (
  <div
    className={`${skeletonBase} rounded-full ${className}`}
    style={{ width: typeof size === 'number' ? `${size}px` : size, height: typeof size === 'number' ? `${size}px` : size }}
  />
);

/** Full-page content placeholder (generic) */
export const ContentPlaceholder: React.FC<{ className?: string; lines?: number }> = ({
  className = '',
  lines = 6,
}) => (
  <div className={`space-y-4 ${className}`}>
    <SkeletonLine height="1.5rem" width="40%" />
    {Array.from({ length: lines }).map((_, i) => (
      <SkeletonLine key={i} width={i === lines - 1 ? '60%' : '100%'} />
    ))}
    <div className="flex gap-3 pt-4">
      <SkeletonBox height="2.5rem" width="6rem" />
      <SkeletonBox height="2.5rem" width="6rem" />
    </div>
  </div>
);

/** Wallet/Balance card placeholder */
export const WalletPlaceholder: React.FC = () => (
  <div className="rounded-2xl bg-white/5 border border-white/10 p-6 space-y-4">
    <div className="flex justify-between">
      <SkeletonLine width="6rem" height="1rem" />
      <SkeletonCircle size="2rem" />
    </div>
    <SkeletonLine width="8rem" height="2rem" />
    <SkeletonLine width="10rem" height="0.875rem" />
    <div className="grid grid-cols-2 gap-3">
      <SkeletonBox height="2.75rem" />
      <SkeletonBox height="2.75rem" />
    </div>
  </div>
);

/** VIP card placeholder */
export const VIPCardPlaceholder: React.FC = () => (
  <div className="rounded-2xl bg-white/5 border border-white/10 p-6 space-y-4">
    <div className="flex items-center gap-3">
      <SkeletonCircle size="3rem" />
      <div className="space-y-1">
        <SkeletonLine width="4rem" height="0.75rem" />
        <SkeletonLine width="6rem" height="1.25rem" />
      </div>
    </div>
    <SkeletonLine width="100%" height="0.875rem" />
    <div className="rounded-xl bg-black/20 p-3 space-y-2">
      <div className="flex justify-between">
        <SkeletonLine width="5rem" height="0.75rem" />
        <SkeletonLine width="2.5rem" height="0.75rem" />
      </div>
      <SkeletonBox height="0.75rem" className="rounded-full" />
    </div>
    <div className="grid grid-cols-2 gap-2">
      <SkeletonBox height="3rem" className="rounded-lg" />
      <SkeletonBox height="3rem" className="rounded-lg" />
    </div>
  </div>
);

/** Bonus page cards placeholder */
export const BonusCardsPlaceholder: React.FC = () => (
  <div className="space-y-6">
    {[1, 2, 3].map((i) => (
      <div key={i} className="rounded-2xl border border-white/10 overflow-hidden">
        <div className="h-32 bg-white/5 p-6 flex items-center gap-4">
          <SkeletonCircle size="4rem" />
          <div className="space-y-2 flex-1">
            <SkeletonLine width="5rem" height="0.875rem" />
            <SkeletonLine width="8rem" height="1.5rem" />
            <SkeletonLine width="6rem" height="0.75rem" />
          </div>
        </div>
        <div className="p-6 bg-white/[0.02] space-y-4">
          <SkeletonLine width="60%" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[1, 2, 3, 4].map((j) => (
              <SkeletonBox key={j} height="4rem" className="rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    ))}
  </div>
);

/** Events list placeholder (sports/esports) */
export const EventsListPlaceholder: React.FC = () => (
  <div className="space-y-3">
    {Array.from({ length: 8 }).map((_, i) => (
      <div key={i} className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/5">
        <SkeletonCircle size="2.5rem" />
        <div className="flex-1 space-y-1">
          <SkeletonLine width="70%" height="0.875rem" />
          <SkeletonLine width="40%" height="0.75rem" />
        </div>
        <SkeletonBox width="4rem" height="2rem" className="rounded-lg" />
      </div>
    ))}
  </div>
);

/** Game grid placeholder (slot/casino pages) – use during initial load, pagination, filter/sort change */
export const GameGridPlaceholder: React.FC<{ count?: number; className?: string }> = ({ count = 20, className = '' }) => (
  <div className={`grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-7 xl:grid-cols-8 2xl:grid-cols-10 gap-2 sm:gap-3 ${className}`}>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="aspect-[3/4] rounded-2xl overflow-hidden">
        <SkeletonBox height="100%" className="rounded-2xl w-full" />
      </div>
    ))}
  </div>
);

/** Free / HTML5 game grid placeholder – card layout with aspect-video thumb + text (minigame free page) */
export const FreeGameGridPlaceholder: React.FC<{ count?: number; className?: string }> = ({ count = 24, className = '' }) => (
  <div className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4 ${className}`}>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="rounded-xl overflow-hidden bg-dark-800 border border-white/10">
        <div className="aspect-video bg-dark-700">
          <SkeletonBox height="100%" className="rounded-none w-full" />
        </div>
        <div className="p-2 sm:p-3 space-y-2">
          <SkeletonLine width="90%" height="0.875rem" />
          <SkeletonLine width="4rem" height="0.75rem" />
        </div>
      </div>
    ))}
  </div>
);

/** Transaction history placeholder – mobile cards + desktop table */
export const TransactionHistoryPlaceholder: React.FC = () => (
  <>
    <div className="md:hidden space-y-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="bg-white/5 rounded-xl p-4 border border-white/5">
          <div className="flex items-start justify-between gap-3 mb-2">
            <SkeletonLine width="5rem" height="0.875rem" />
            <SkeletonLine width="4rem" height="1rem" />
          </div>
          <div className="flex gap-2">
            <SkeletonLine width="6rem" height="0.75rem" />
            <SkeletonLine width="3rem" height="1.25rem" className="rounded-full" />
          </div>
        </div>
      ))}
    </div>
    <div className="hidden md:block overflow-x-auto">
      <div className="flex gap-4 p-4 border-b border-white/10 bg-white/5">
        {[1, 2, 3, 4, 5].map((i) => (
          <SkeletonLine key={i} width={i === 1 ? '2rem' : '4rem'} height="0.875rem" />
        ))}
      </div>
      <div className="divide-y divide-white/5">
        {Array.from({ length: 6 }).map((_, r) => (
          <div key={r} className="flex gap-4 p-4">
            {[1, 2, 3, 4, 5].map((_, c) => (
              <SkeletonLine key={c} width={c === 0 ? '2rem' : '4rem'} height="0.75rem" />
            ))}
          </div>
        ))}
      </div>
    </div>
  </>
);

/** Bet history table placeholder */
export const TablePlaceholder: React.FC<{ rows?: number; cols?: number }> = ({ rows = 6, cols = 5 }) => (
  <div className="overflow-hidden rounded-xl border border-white/10">
    <div className="flex gap-4 p-4 border-b border-white/10 bg-white/5">
      {Array.from({ length: cols }).map((_, i) => (
        <SkeletonLine key={i} width={i === 0 ? '8rem' : '4rem'} height="0.875rem" />
      ))}
    </div>
    <div className="divide-y divide-white/5">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-4 p-4">
          {Array.from({ length: cols }).map((_, c) => (
            <SkeletonLine key={c} width={c === 0 ? '6rem' : '3rem'} height="0.75rem" />
          ))}
        </div>
      ))}
    </div>
  </div>
);

/** Notice page list placeholder */
export const NoticeListPlaceholder: React.FC = () => (
  <div className="space-y-4">
    {Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className="rounded-xl border border-white/10 p-4 space-y-2">
        <SkeletonLine width="60%" height="1.125rem" />
        <SkeletonLine width="100%" height="0.75rem" />
        <SkeletonLine width="80%" height="0.75rem" />
      </div>
    ))}
  </div>
);

/** Message / Question list – table rows placeholder (desktop). cols: 4 = message, 5 = question */
export const MessageTableRowsPlaceholder: React.FC<{ rows?: number; cols?: number }> = ({ rows = 6, cols = 4 }) => (
  <>
    {Array.from({ length: rows }).map((_, r) => (
      <tr key={r} className="border-b border-white/5">
        <td className="py-3 px-4"><SkeletonLine width="2rem" height="0.75rem" /></td>
        <td className="py-3 px-4"><SkeletonLine width="5rem" height="0.75rem" /></td>
        <td className="py-3 px-4"><SkeletonLine width={r % 2 ? '70%' : '50%'} height="0.875rem" /></td>
        {cols >= 4 && <td className="py-3 px-4"><SkeletonLine width="4rem" height="1.25rem" className="rounded" /></td>}
        {cols >= 5 && <td className="py-3 px-4"><SkeletonLine width="3rem" height="0.75rem" /></td>}
      </tr>
    ))}
  </>
);

/** Message / Question list – card rows for mobile */
export const MessageListPlaceholder: React.FC<{ count?: number }> = ({ count = 6 }) => (
  <div className="space-y-3">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="bg-white/5 rounded-lg p-3 space-y-2">
        <div className="flex justify-between items-center">
          <SkeletonLine width="2rem" height="0.625rem" />
          <SkeletonLine width="4rem" height="1.25rem" className="rounded" />
        </div>
        <SkeletonLine width="4rem" height="0.5rem" />
        <SkeletonLine width={i % 2 ? '90%' : '70%'} height="0.875rem" />
      </div>
    ))}
  </div>
);

/** Sidebar sport list placeholder */
export const SidebarListPlaceholder: React.FC = () => (
  <div className="space-y-1 py-2">
    {Array.from({ length: 6 }).map((_, i) => (
      <div key={i} className="flex items-center gap-2 px-3 py-2">
        <SkeletonCircle size="1.25rem" />
        <SkeletonLine width="4rem" height="0.625rem" />
      </div>
    ))}
  </div>
);

/** Event detail page placeholder (header + markets) */
export const EventDetailPlaceholder: React.FC = () => (
  <div className="min-h-screen bg-gradient-to-b from-dark-900 via-dark-900 to-dark-950 text-white">
    <div className="relative overflow-hidden bg-gradient-to-r from-emerald-500/20 to-transparent">
      <div className="absolute inset-0 bg-dark-900/80" />
      <div className="relative container mx-auto px-4 py-6">
        <div className="flex items-center gap-2 mb-4">
          <SkeletonBox width="2rem" height="1.25rem" className="rounded" />
          <SkeletonLine width="4rem" height="1rem" />
        </div>
        <div className="flex items-center gap-2 mb-6">
          <SkeletonCircle size="1.5rem" />
          <SkeletonLine width="8rem" height="0.875rem" />
        </div>
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1 flex flex-col items-center gap-2">
            <SkeletonLine width="80%" height="1.5rem" />
            <SkeletonLine width="3rem" height="2rem" className="rounded" />
          </div>
          <div className="px-4 flex flex-col items-center gap-1">
            <SkeletonLine width="1.5rem" height="0.875rem" />
            <SkeletonLine width="4rem" height="0.75rem" />
          </div>
          <div className="flex-1 flex flex-col items-center gap-2">
            <SkeletonLine width="80%" height="1.5rem" />
            <SkeletonLine width="3rem" height="2rem" className="rounded" />
          </div>
        </div>
      </div>
    </div>
    <div className="container mx-auto px-4 py-6">
      <div className="flex flex-wrap gap-2 mb-6">
        {[1, 2, 3, 4].map((i) => (
          <SkeletonBox key={i} width="5rem" height="2.25rem" className="rounded-lg" />
        ))}
      </div>
      <div className="space-y-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="rounded-xl bg-white/5 border border-white/5 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <SkeletonLine width="10rem" height="1rem" />
              <SkeletonCircle size="1.25rem" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[1, 2, 3].map((j) => (
                <SkeletonBox key={j} height="2.5rem" className="rounded-lg" />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);
